// src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
    Paper,
    Switch,
    Avatar,
    Divider,
} from '@mui/material';
import {
    Assignment as FormIcon,
    EmojiEvents as TrophyIcon,
    People as PeopleIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Event as EventIcon,
    Assessment as AssessmentIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import AdminLayout from './AdminLayout';
import AdminOnboardingPanel from './admin/AdminOnboardingPanel';
import AdminAssessmentPanel from './admin/AdminAssessmentPanel';
import AdminCreditPanel from './admin/AdminCreditPanel';
import AdminQuizManager from './admin/AdminQuizManager';
import AdminNoticeBoardPanel from '../components/notices/AdminNoticeBoardPanel';

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [events, setEvents] = useState([]);
    const [users, setUsers] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [publishStatus, setPublishStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch forms
            const formsSnapshot = await getDocs(collection(db, 'forms'));
            const formsList = formsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setForms(formsList);

            // Initialize publish status
            const statusMap = {};
            formsList.forEach(form => {
                statusMap[form.id] = form.isPublished || false;
            });
            setPublishStatus(statusMap);

            // Fetch competitions
            const compSnapshot = await getDocs(collection(db, 'competitions'));
            const compsList = compSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCompetitions(compsList);

            // Fetch events
            const eventsSnapshot = await getDocs(collection(db, 'events'));
            const eventsList = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEvents(eventsList);

            // Fetch users
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(usersList);

            // Fetch submissions
            const submissionsSnapshot = await getDocs(collection(db, 'submissions'));
            const submissionsList = submissionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSubmissions(submissionsList);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePublish = async (formId) => {
        try {
            const newStatus = !publishStatus[formId];
            await updateDoc(doc(db, 'forms', formId), {
                isPublished: newStatus
            });
            setPublishStatus(prev => ({ ...prev, [formId]: newStatus }));
        } catch (error) {
            console.error('Error toggling publish status:', error);
        }
    };

    const publishedFormsCount = Object.values(publishStatus).filter(s => s).length;
    const activeCompetitionsCount = competitions.filter(comp =>
        new Date(comp.lastDateToApply) > new Date()
    ).length;

    return (
        <AdminLayout title="Dashboard">
            <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
                <Container maxWidth="xl">
                    {/* Header */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                                    Admin Dashboard
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Manage forms, events, competitions, and user activities
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/admin/forms/create')}
                                sx={{
                                    bgcolor: '#2563eb',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    '&:hover': {
                                        bgcolor: '#1e40af'
                                    }
                                }}
                            >
                                Create New
                            </Button>
                        </Box>
                    </Paper>

                    <AdminNoticeBoardPanel />

                    {/* Stats Grid */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                                onClick={() => navigate('/admin/forms')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: '#eff6ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FormIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                                    </Box>
                                    <Chip
                                        label={publishedFormsCount}
                                        size="small"
                                        sx={{
                                            bgcolor: '#dcfce7',
                                            color: '#16a34a',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                                    {forms.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Forms
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                                onClick={() => navigate('/admin/competitions')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: '#fef2f2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <TrophyIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                                    </Box>
                                    <Chip
                                        label={`${activeCompetitionsCount} Active`}
                                        size="small"
                                        sx={{
                                            bgcolor: '#fef2f2',
                                            color: '#dc2626',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                                    {competitions.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Competitions
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                                onClick={() => navigate('/admin/events-v2')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: '#eff6ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <EventIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                                    {events.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Events
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                                onClick={() => navigate('/admin/users')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: '#fef2f2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <PeopleIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                                    {users.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Users
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Quick Actions */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 3 }}>
                            Quick Actions
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/admin/forms/create')}
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e5e7eb',
                                        color: '#2563eb',
                                        '&:hover': {
                                            borderColor: '#2563eb',
                                            bgcolor: '#eff6ff'
                                        }
                                    }}
                                >
                                    Create Form
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/admin/events-v2')}
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e5e7eb',
                                        color: '#dc2626',
                                        '&:hover': {
                                            borderColor: '#dc2626',
                                            bgcolor: '#fef2f2'
                                        }
                                    }}
                                >
                                    Create Event
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/admin/competitions/new')}
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e5e7eb',
                                        color: '#2563eb',
                                        '&:hover': {
                                            borderColor: '#2563eb',
                                            bgcolor: '#eff6ff'
                                        }
                                    }}
                                >
                                    Create Competition
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<AssessmentIcon />}
                                    onClick={() => navigate('/admin/submissions')}
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        bgcolor: '#dc2626',
                                        '&:hover': {
                                            bgcolor: '#b91c1c'
                                        }
                                    }}
                                >
                                    View Reports
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Forms Management */}
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: 'white',
                            borderRadius: 2,
                            border: '1px solid #e5e7eb',
                            mb: 4
                        }}
                    >
                        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 1.5,
                                            bgcolor: '#eff6ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FormIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                                        Forms Management
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/admin/forms/new')}
                                    size="small"
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e5e7eb',
                                        color: '#2563eb',
                                        '&:hover': {
                                            borderColor: '#2563eb',
                                            bgcolor: '#eff6ff'
                                        }
                                    }}
                                >
                                    New Form
                                </Button>
                            </Box>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            {forms.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {forms.slice(0, 5).map((form) => (
                                        <Paper
                                            key={form.id}
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 2,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                    borderColor: publishStatus[form.id] ? '#16a34a' : '#2563eb'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                                                            {form.title}
                                                        </Typography>
                                                        <Chip
                                                            icon={publishStatus[form.id] ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
                                                            label={publishStatus[form.id] ? 'Published' : 'Draft'}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: publishStatus[form.id] ? '#dcfce7' : '#f3f4f6',
                                                                color: publishStatus[form.id] ? '#16a34a' : '#6b7280',
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                height: 22,
                                                                '& .MuiChip-icon': {
                                                                    color: publishStatus[form.id] ? '#16a34a' : '#6b7280',
                                                                    fontSize: 14
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {form.description || 'No description'}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                                    <Switch
                                                        checked={publishStatus[form.id]}
                                                        onChange={() => handleTogglePublish(form.id)}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: '#16a34a'
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                bgcolor: '#16a34a'
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => navigate(`/admin/forms/${form.id}`)}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderColor: '#e5e7eb',
                                                            color: '#6b7280',
                                                            minWidth: 80,
                                                            '&:hover': {
                                                                borderColor: '#2563eb',
                                                                color: '#2563eb',
                                                                bgcolor: '#eff6ff'
                                                            }
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 2,
                                            bgcolor: '#f3f4f6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}
                                    >
                                        <FormIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                                        No Forms Created
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Get started by creating your first form
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate('/admin/forms/new')}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            bgcolor: '#2563eb',
                                            '&:hover': {
                                                bgcolor: '#1e40af'
                                            }
                                        }}
                                    >
                                        Create First Form
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Paper>

                    {/* Events & Competitions */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    height: '100%'
                                }}
                            >
                                <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.5,
                                                    bgcolor: '#fef2f2',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <EventIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                                                Events
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={events.length}
                                            size="small"
                                            sx={{
                                                bgcolor: '#fef2f2',
                                                color: '#dc2626',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ p: 3, maxHeight: 400, overflowY: 'auto' }}>
                                    {events.slice(0, 5).map((event) => (
                                        <Paper
                                            key={event.id}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                    borderColor: '#dc2626'
                                                }
                                            }}
                                            onClick={() => navigate(`/admin/events-v2`)}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                                                {event.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {event.date ? new Date(event.date).toLocaleDateString() : 'No date set'}
                                            </Typography>
                                        </Paper>
                                    ))}
                                    {events.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                                            No events created
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    border: '1px solid #e5e7eb',
                                    height: '100%'
                                }}
                            >
                                <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.5,
                                                    bgcolor: '#eff6ff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <TrophyIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                                                Competitions
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${activeCompetitionsCount} Active`}
                                            size="small"
                                            sx={{
                                                bgcolor: '#eff6ff',
                                                color: '#2563eb',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ p: 3, maxHeight: 400, overflowY: 'auto' }}>
                                    {competitions.slice(0, 5).map((comp) => {
                                        const isActive = new Date(comp.lastDateToApply) > new Date();
                                        return (
                                            <Paper
                                                key={comp.id}
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    mb: 2,
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    opacity: isActive ? 1 : 0.6,
                                                    '&:hover': {
                                                        boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                                                        borderColor: isActive ? '#2563eb' : '#e5e7eb'
                                                    }
                                                }}
                                                onClick={() => navigate(`/admin/competitions/${comp.id}`)}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                                                            {comp.title}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {comp.lastDateToApply ? new Date(comp.lastDateToApply).toLocaleDateString() : 'No deadline'}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={isActive ? 'Active' : 'Closed'}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: isActive ? '#dcfce7' : '#f3f4f6',
                                                            color: isActive ? '#16a34a' : '#6b7280',
                                                            fontSize: '0.7rem',
                                                            height: 22,
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                    {competitions.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                                            No competitions created
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Onboarding & Assessment & Credits */}
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <AdminOnboardingPanel onSelectUser={(u) => setSelectedUser(u)} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AdminAssessmentPanel user={selectedUser} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AdminCreditPanel user={selectedUser} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AdminQuizManager />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </AdminLayout>
    );
}
